import jsPDF from "jspdf";
import DateConverter from "@heartinz/date_and_time_processor";
import uRLs from "../../baseUtils";
import QRCode from "qrcode";
import autoTable from "jspdf-autotable";
import { usePermission } from "../../../context/PermissionContext";
import { useAuth } from "../../../context/AuthProvider";
import { FormatedSaleData } from "../GlobelSaleData";
import { capitalizeFirstLetter } from "../a4Invoices/common/invoiceUtils";
import { useEffect, useState } from "react";

const loadImageDimensions = (base64Src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = base64Src;
  });
};
const urlToBase64 = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// invoice
export const generateIN_HA4_3PDF = async (
  data,
  storeSettings,
  selectedTheme,
  optData,
) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const centerX = pageW / 2;
  let y = PADDING; // Starting height
  const isBillReceipt =
    data?.salemode?.toUpperCase() === "BILL" &&
    storeSettings?.saleSettings?.saleModeSettings?.isBill_isTax;
  const documentTitle = isBillReceipt ? "BILL RECEIPT" : "TAX INVOICE";
  const invLabels = isBillReceipt ? "Bill No:" : "Invoice No:";

  if (data?.salefrom?.image) {
    try {
      const imagePath = uRLs?.IMAGE_URL + "profile/" + data?.salefrom?.image;
      const logoBase64 = await urlToBase64(imagePath);
      const { width, height } = await loadImageDimensions(logoBase64);
      // 1. DRAW LOGO (Far Left)
      const maxWidth = 30;
      const maxHeight = 12;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const finalW = width * ratio;
      const finalH = height * ratio;

      doc.addImage(logoBase64, "PNG", MARGIN_LEFT, y, finalW, finalH);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(documentTitle, centerX, y + 3, { align: "center" });

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(data.salefrom?.title?.toUpperCase() || "", centerX, y + 8, {
        align: "center",
      });

      doc.setFontSize(7);
      doc.setTextColor(0);

      // Invoice No Row

      const invLabel = invLabels;
      const invVal = data.saleno || "-";
      const timezone = data?.saleby?.timezone;
      const invDateTime = DateConverter.ISOToDisplayFormat(
        DateConverter.convertToTimeZone(data?.saledate, timezone),
      ).time;
      const dateStr = `DATE: ${data.saledate ? DateConverter.ISOToDisplayFormat(data.saledate).date : "-"}  `;
      const timeStr = `TIME: ${invDateTime || ""}`;
      const invValW = doc.getTextWidth(invVal);
      doc.setFont("Inter-Regular", "normal");
      doc.text(
        invLabel,
        MARGIN_RIGHT - invValW - doc.getTextWidth(invLabel),
        y + 2,
      );
      doc.setFont("Inter-Bold", "bold");
      doc.text(invVal, MARGIN_RIGHT, y + 2, { align: "right" });
      const totalDateTimeWidth =
        doc.getTextWidth(dateStr) + doc.getTextWidth(timeStr);

      const lineStartX = MARGIN_RIGHT - totalDateTimeWidth - 1; // Adjust this number to make the line longer/shorter
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor); // Light grey color
      doc.setLineWidth(0.1);
      doc.line(lineStartX, y + 3.5, MARGIN_RIGHT, y + 3.5);

      const timeStrW = doc.getTextWidth(timeStr);
      doc.setFont("Inter-Regular", "normal");
      doc.text(dateStr, MARGIN_RIGHT - timeStrW - 1, y + 6, { align: "right" });
      doc.text(timeStr, MARGIN_RIGHT, y + 6, { align: "right" });

      let addrY = y + finalH + 4; // Move down below logo/title area
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(60, 60, 60);
      let currentTextY = addrY;
      const addrLine1 = [
        data.salefrom?.addressline1,
        data.salefrom?.addressline2,
      ]
        .filter(Boolean)
        .join(", ");
      const addrLine2 = [
        data.salefrom?.district,
        data.salefrom?.state,
        data.salefrom?.pincode,
      ]
        .filter(Boolean)
        .join(", ");
      const contactLine = `Ph: ${data.salefrom?.phone || ""}  GSTIN: ${data.salefrom?.gstnumber || ""}`;
      // const contactY = addrY + 6;
      if (addrLine1.trim()) {
        doc.text(addrLine1, MARGIN_LEFT, currentTextY);
        currentTextY += 3; // Only move down if we actually printed something
      }
      if (addrLine2.trim()) {
        doc.text(addrLine2, MARGIN_LEFT, currentTextY);
        currentTextY += 3;
      }
      let currentX = MARGIN_LEFT;
      const phone = data.salefrom?.phone;
      const gstin = data.salefrom?.gstnumber;

      if (phone || gstin) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Ph:", currentX, currentTextY);
        currentX += doc.getTextWidth("Ph: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(`${data.salefrom?.phone || ""}  `, currentX, currentTextY);
        currentX += doc.getTextWidth(`${phone || ""}  `);

        doc.setFont("Inter-Bold", "bold");
        doc.text("GSTIN:", currentX, currentTextY);
        currentX += doc.getTextWidth("GSTIN: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(`${gstin || ""}`, currentX, currentTextY);
        // doc.text(contactLine, MARGIN_LEFT, addrY + 6);
      }
      y = currentTextY + 2;
    } catch (e) {
      console.warn("Header layout failed", e);
      y += 15;
    }
  }

  y += 1;

  if (optData.isCustomerDisplay && data.saleto) {
  let rowHeight = 6;

  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor || [200, 200, 200]
  );
  doc.setLineWidth(0.2);

  // ---------------- LEFT ----------------
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text("Customer Details :", MARGIN_LEFT + 2, y + 4);

  doc.setFont("Inter-Regular", "normal");
  doc.text(data.saleto?.name || "", MARGIN_LEFT + 2, y + 8);

  rowHeight += 4;

  const fullAddress = [
    data.saleto?.addressline1,
    data.saleto?.addressline2,
    data.saleto?.district,
    data.saleto?.state,
  ]
    .filter(Boolean)
    .join(", ");

  const pinCode = data.saleto?.pincode
    ? ` - ${data.saleto.pincode}`
    : "";

  if (fullAddress || pinCode) {
    const wrapped = doc.splitTextToSize(
      fullAddress + pinCode,
      pageW / 2 + 10
    );

    doc.text(wrapped, MARGIN_LEFT + 2, y + 12);

    rowHeight += wrapped.length * 3.5;
  }

  // ---------------- RIGHT ----------------

  const mrpStartX = MARGIN_LEFT + 143;
  const rightSectionStart = mrpStartX + 2;

  const customerInfo = [
    { label: "Ph:", value: data.saleto?.phone },
    { label: "GSTIN:", value: data.saleto?.gstnumber },
    { label: "Reference No:", value: data.saleto?.referenceid },
    { label: "FSSAI:", value: data.saleto?.fssai },
    { label: "CIN:", value: data.saleto?.cinnumber },
    { label: "PAN:", value: data.saleto?.pannumber },
  ].filter(item => item.value);

  let infoY = y + 4;

  customerInfo.forEach(({ label, value }) => {

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7.5);
    doc.text(label, rightSectionStart, infoY);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      String(value),
      rightSectionStart + doc.getTextWidth(label) + 1,
      infoY
    );

    infoY += 4.5;
  });

  // Increase height if right side is taller
  rowHeight = Math.max(rowHeight, infoY - y + 1);

  // Draw divider AFTER calculating final height
  doc.line(mrpStartX, y, mrpStartX, y + rowHeight);

  // Draw outer rectangle
  doc.rect(
    MARGIN_LEFT,
    y,
    MARGIN_RIGHT - MARGIN_LEFT,
    rowHeight
  );

  y += rowHeight;
}
  /* -------------------------------------------------
     PRODUCT TABLE1
  ------------------------------------------------- */

  const rowCount = data.saleitems.length;
  const standardHeight = 4;
  const targetTotalHeight = 40;
  const extraSpace =
    rowCount > 0 && rowCount <= 5
      ? targetTotalHeight - rowCount * standardHeight
      : 0;
  autoTable(doc, {
    startY: y,
    theme: "plain",
    rowPageBreak: "avoid",
    margin: {
      top: PADDING,
      left: PADDING,
      right: PADDING,
      bottom: pageH - HALF_PAGE_HEIGHT + PADDING,
    },
    styles: {
      font: "Inter-Regular",
      fontSize: 6.5,
      cellPadding: 1.5,
      valign: "top",
      lineWidth: 0, // No default borders
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      fillColor: selectedTheme?.templateTheme?.primaryColor || [216, 27, 96], // Pink like image
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      lineWidth: 0.2,
      lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    },
    bodyStyles: {
      minCellHeight: Math.max(2, standardHeight),
      valign: "top",
    },
    head: [["#", "ITEM", "RATE", "QTY", "LANDING  COST", "Tax Val", "AMOUNT"]],
    body: data.saleitems.map((it, i) => [
      i + 1,
      it.hsnsac ? `${it.title}\nHSN: ${it.hsnsac}` : it.title,

      `₹${it.baseprice}`,
      `${it.quantity} ${(() => {
        const unitCode = it?.isSecondaryUnit
          ? it?.unitConfig?.secondaryUnitCode
          : it?.unitConfig?.primaryUnitCode;
        return unitCode || "";
      })()}`,
      `₹${it.lineTotalLandingCost}`,
      `₹${it.lineTotalBasePrice}`,
      `₹${it.lineGrandTotal}`,
    ]),
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 15, halign: "right" },
      3: { cellWidth: 11, halign: "center" },
      4: { cellWidth: 25, halign: "right" },
      5: { cellWidth: 16, halign: "right" },
      6: { cellWidth: 16, halign: "right" },
    },
    didParseCell: function (data) {
      if (data.section === "head") {
        const colId = data.column.index;
        if (colId === 0 || colId === 3) data.cell.styles.halign = "center";
        if (colId === 2 || colId === 4 || colId === 5 || colId === 6)
          data.cell.styles.halign = "right";
      }
      if (data.section === "body") {
        data.cell.styles.lineWidth = {
          left: 0.1,
          right: 0.1,
          top: 0,
          bottom: 0,
        };
        data.cell.styles.lineColor = selectedTheme?.templateTheme
          ?.borderColor || [200, 200, 200]; // Black vertical lines
        if (data.row.index === rowCount - 1) {
          data.cell.styles.minCellHeight = standardHeight + extraSpace;
        }
        if (data.row.index === rowCount - 1) {
          data.cell.styles.lineWidth.bottom = 0.1;
        }
      }
    },
    didDrawPage: function (data) {
      const finalY = data.cursor.y;
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
      doc.line(PADDING, finalY, pageW - PADDING, finalY);
    },
  });
  let finalY = doc.lastAutoTable.finalY;
  const isInterState =
    data?.salefrom?.statecode &&
    data?.saleto?.statecode &&
    data?.salefrom?.statecode !== data?.saleto?.statecode;

  const borderColor = selectedTheme?.templateTheme?.borderColor || [
    200, 200, 200,
  ];
  const rowH = 5;
  const sigSectionHeight = 15;
  const taxRowsCount = isInterState ? 1 : 2;
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.1);
  const totalFooterHeight =
    rowH + Math.max(sigSectionHeight, taxRowsCount * rowH) + 10; // +10 for branding/padding
  if (finalY + totalFooterHeight > HALF_PAGE_HEIGHT - 5) {
    doc.addPage();
    finalY = PADDING;
  }

  const rightSectionWidth = 57;
  const amountWidth = 16;
  const totalLabelX = MARGIN_RIGHT - rightSectionWidth;
  const totalValueX = MARGIN_RIGHT - amountWidth;

  // --- 2. TOP ROW: IN WORDS & TOTAL ---
  doc.rect(PADDING, finalY, pageW - PADDING * 2, rowH);
  doc.line(totalLabelX, finalY, totalLabelX, finalY + rowH);
  doc.line(totalValueX, finalY, totalValueX, finalY + rowH);

  let textY = finalY + rowH / 2 + 1;
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(7);
  doc.text("In Words:", PADDING + 2, textY);
  doc.setFont("Inter-Regular", "normal");
  const words = data?.amountinwords || "Zero Rupees Only";
  doc.text(words, PADDING + 15, textY, {
    maxWidth: totalLabelX - PADDING - 17,
  });

  doc.setFont("Inter-Bold", "bold");
  doc.text("TOTAL", totalLabelX + 2, textY);
  doc.text(`₹${data?.grandtotal}`, MARGIN_RIGHT - 2, textY, { align: "right" });

  // --- 3. THE "SAME LINE" SECTION (Signature Left, Taxes Right) ---
  let currentTaxY = finalY + rowH; // This is the shared starting line

  // Total height for this area
  const totalHeight = Math.max(sigSectionHeight, taxRowsCount * rowH);
  if (isInterState) {
    doc.rect(totalLabelX, currentTaxY, rightSectionWidth, rowH);
    doc.line(totalValueX, currentTaxY, totalValueX, currentTaxY + rowH);
    doc.setFont("Inter-Bold", "bold");
    doc.text("IGST", totalLabelX + 2, currentTaxY + 3.5);
    doc.setFont("Inter-Regular", "normal");
    doc.text(`₹${data?.igstamount || 0}`, MARGIN_RIGHT - 2, currentTaxY + 3.5, {
      align: "right",
    });
  } else {
    // CGST Row
    doc.rect(totalLabelX, currentTaxY, rightSectionWidth, rowH);
    doc.line(totalValueX, currentTaxY, totalValueX, currentTaxY + rowH);
    doc.setFont("Inter-Regular", "normal");
    doc.text("CGST", totalLabelX + 2, currentTaxY + 3.5);
    doc.setFont("Inter-Regular", "normal");
    doc.text(`₹${data?.cgstamount || 0}`, MARGIN_RIGHT - 2, currentTaxY + 3.5, {
      align: "right",
    });

    // SGST Row (Directly below CGST)
    doc.rect(totalLabelX, currentTaxY + rowH, rightSectionWidth, rowH);
    doc.line(
      totalValueX,
      currentTaxY + rowH,
      totalValueX,
      currentTaxY + rowH * 2,
    );
    doc.setFont("Inter-Regular", "normal");
    doc.text("SGST", totalLabelX + 2, currentTaxY + rowH + 3.5);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      `₹${data?.sgstamount || 0}`,
      MARGIN_RIGHT - 2,
      currentTaxY + rowH + 3.5,
      { align: "right" },
    );
  }
  let sigBottomY = currentTaxY + totalHeight - 2;
 const paymentData = [
    { amount: data?.cashPaid || 0, label: "Cash", val: data?.cashPaid },
    { amount: data?.cardPaid || 0, label: "Card", val: data?.cardPaid },
    { amount: data?.upiPaid || 0, label: "UPI", val: data?.upiPaid },
    { amount: data?.onlinePaid || 0, label: "Online", val: data?.onlinePaid },
  ].filter((p) => p.amount > 0);
  currentTaxY+=5
   let paymentEndY = currentTaxY;

if (optData?.isPayment && paymentData.length > 0) {
  paymentData.forEach((pay, index) => {
    const xPos = index % 2 === 0 ? PADDING : PADDING + 45;
    const yPos = paymentEndY + Math.floor(index / 2) * 5;

    doc.setFont("Inter-Bold", "bold");
    doc.text(`₹${pay.val}`, xPos, yPos);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      ` Paid via ${pay.label}`,
      xPos + doc.getTextWidth(`₹${pay.val}`) + 1,
      yPos
    );
  });

  // Calculate payment section height
  const paymentRows = Math.ceil(paymentData.length / 2);
  paymentEndY = sigBottomY + paymentRows * 5 -5;
}
 let currentSectionY = paymentEndY ;
  // let currentSectionY = currentTaxY + totalHeight + 5;
  if (storeSettings?.paymentSettings?.bankDetails && optData?.isBankDetails) {
      const bankStartY = currentSectionY;
  
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0);
      doc.text("Bank Details:", PADDING , bankStartY);
  
      const bankInfo = [
        ["Name:", storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || ""],
        ["Account #:", storeSettings?.paymentSettings?.bankDetails?.accountNo || ""],
        ["IFSC:", storeSettings?.paymentSettings?.bankDetails?.ifsc || ""],
        ["UPI ID:", storeSettings?.paymentSettings?.upiId || ""],
      ];
  
      let currentBankY = bankStartY + 4;
      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(label, PADDING, currentBankY);
        
        doc.setFont("Inter-Regular", "normal");
        doc.setTextColor(0);
        doc.text(String(value || ""), PADDING + 20, currentBankY);
        
        currentBankY += 3.8;
      });
  
      // Generate & Draw QR Code alongside Bank Details
      const upiId = storeSettings?.paymentSettings?.upiId;
      const beneficiaryName = storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";
  
      if (upiId) {
        try {
          const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(beneficiaryName)}&cu=INR`;
          const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
            errorCorrectionLevel: "L",
            margin: 1,
            scale: 4,
          });
  
          const qrSize = 18;
          const qrX = 90 - qrSize - 4; // Positioned before vertical line
          const qrY = bankStartY + 1;
  
          doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
        } catch (err) {
          console.warn("QR Code generation failed", err);
        }
      }
  
      currentSectionY = currentBankY;
    }
     if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
      const base64 = await urlToBase64(signimg);
      const { width, height } = await loadImageDimensions(base64);
      const ratio = Math.min(25 / width, 8 / height);
      const imgW = width * ratio;
      const imgH = height * ratio;
        sigBottomY += 3;
      doc.addImage(base64, "PNG",   MARGIN_RIGHT - imgW, sigBottomY, imgW, imgH);
    } catch (e) {
      console.warn("Signature failed", e);
    }
  } else {
    sigBottomY = currentTaxY + totalHeight - 3;
  }

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(6);
 sigBottomY += 10;
  doc.text("Authorised Signature", MARGIN_RIGHT, sigBottomY, {
    align: "right",
  });
  if (optData?.isRemark && data?.remark?.trim()) {

  const remarkWidth = pageW - (PADDING * 2);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);

  const remarkLines = doc.splitTextToSize(
    capitalizeFirstLetter(data.remark),
    remarkWidth - 6
  );

  const lineHeight = 3.5;
  const paddingTop = 2;
  const paddingBottom = 2;

  const remarkHeight =
    (remarkLines.length * lineHeight) + paddingTop + paddingBottom;

  if (
    currentSectionY + remarkHeight + 20 >
    HALF_PAGE_HEIGHT - 5
  ) {
    doc.addPage();
    currentSectionY = PADDING + 5;
  }

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text("Remark", PADDING, currentSectionY);

  currentSectionY += 2;

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);

  let remarkY = currentSectionY + 2;

  remarkLines.forEach(line => {
    doc.text(line, PADDING , remarkY);
    remarkY += lineHeight;
  });

  currentSectionY += remarkHeight + 4;
}

  if (data?.negobillBranding) {
    const minGapY = y + 3;
    const bottomLimitY = HALF_PAGE_HEIGHT - 5;
    let finalBrandingY = Math.max(minGapY, bottomLimitY);
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);
    doc.text(normalText, MARGIN_LEFT, finalBrandingY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, MARGIN_LEFT + normalW, finalBrandingY);
  }

  return doc;
};

export const generateBILL_HA4_3PDF = async (
  data,
  storeSettings,
  selectedTheme,
  optData,
) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const centerX = pageW / 2;
  let y = PADDING; // Starting height

  if (data?.salefrom?.image) {
    try {
      const imagePath = uRLs?.IMAGE_URL + "profile/" + data?.salefrom?.image;
      const logoBase64 = await urlToBase64(imagePath);
      const { width, height } = await loadImageDimensions(logoBase64);
      // 1. DRAW LOGO (Far Left)
      const maxWidth = 30;
      const maxHeight = 12;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const finalW = width * ratio;
      const finalH = height * ratio;

      doc.addImage(logoBase64, "PNG", MARGIN_LEFT, y, finalW, finalH);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text("BILL RECEIPT", centerX, y + 3, { align: "center" });

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(data.salefrom?.title?.toUpperCase() || "", centerX, y + 8, {
        align: "center",
      });

      doc.setFontSize(7);
      doc.setTextColor(0);

      // Invoice No Row

      const invLabel = "Bill No. ";
      const invVal = data.saleno || "-";
      const timezone = data?.saleby?.timezone;
      const invDateTime = DateConverter.ISOToDisplayFormat(
        DateConverter.convertToTimeZone(data?.saledate, timezone),
      ).time;
      const dateStr = `DATE: ${data.saledate ? DateConverter.ISOToDisplayFormat(data.saledate).date : "-"}  `;
      const timeStr = `TIME: ${invDateTime || ""}`;
      const invValW = doc.getTextWidth(invVal);
      doc.setFont("Inter-Regular", "normal");
      doc.text(
        invLabel,
        MARGIN_RIGHT - invValW - doc.getTextWidth(invLabel),
        y + 2,
      );
      doc.setFont("Inter-Bold", "bold");
      doc.text(invVal, MARGIN_RIGHT, y + 2, { align: "right" });
      const totalDateTimeWidth =
        doc.getTextWidth(dateStr) + doc.getTextWidth(timeStr);

      const lineStartX = MARGIN_RIGHT - totalDateTimeWidth - 1; // Adjust this number to make the line longer/shorter
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor); // Light grey color
      doc.setLineWidth(0.1);
      doc.line(lineStartX, y + 3.5, MARGIN_RIGHT, y + 3.5);

      const timeStrW = doc.getTextWidth(timeStr);
      doc.setFont("Inter-Regular", "normal");
      doc.text(dateStr, MARGIN_RIGHT - timeStrW - 1, y + 6, { align: "right" });
      doc.text(timeStr, MARGIN_RIGHT, y + 6, { align: "right" });

      let addrY = y + finalH + 4; // Move down below logo/title area
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(60, 60, 60);
      let currentTextY = addrY;
      const addrLine1 = [
        data.salefrom?.addressline1,
        data.salefrom?.addressline2,
      ]
        .filter(Boolean)
        .join(", ");
      const addrLine2 = [
        data.salefrom?.district,
        data.salefrom?.state,
        data.salefrom?.pincode,
      ]
        .filter(Boolean)
        .join(", ");
      // const contactY = addrY + 6;
      if (addrLine1.trim()) {
        doc.text(addrLine1, MARGIN_LEFT, currentTextY);
        currentTextY += 3; // Only move down if we actually printed something
      }
      if (addrLine2.trim()) {
        doc.text(addrLine2, MARGIN_LEFT, currentTextY);
        currentTextY += 3;
      }
      let currentX = MARGIN_LEFT;
      const phone = data.salefrom?.phone;

      if (phone) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Ph:", currentX, currentTextY);
        currentX += doc.getTextWidth("Ph: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(`${data.salefrom?.phone || ""}  `, currentX, currentTextY);
        currentX += doc.getTextWidth(`${phone || ""}  `);

        // doc.text(contactLine, MARGIN_LEFT, addrY + 6);
      }
      y = currentTextY + 2;
    } catch (e) {
      console.warn("Header layout failed", e);
      y += 15;
    }
  }

  y += 1;

  if (optData.isCustomerDisplay && data.saleto) {
  let rowHeight = 6;

  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor || [200, 200, 200]
  );
  doc.setLineWidth(0.2);

  // ---------------- LEFT ----------------
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text("Customer Details :", MARGIN_LEFT + 2, y + 4);

  doc.setFont("Inter-Regular", "normal");
  doc.text(data.saleto?.name || "", MARGIN_LEFT + 2, y + 8);

  rowHeight += 4;

  const fullAddress = [
    data.saleto?.addressline1,
    data.saleto?.addressline2,
    data.saleto?.district,
    data.saleto?.state,
  ]
    .filter(Boolean)
    .join(", ");

  const pinCode = data.saleto?.pincode
    ? ` - ${data.saleto.pincode}`
    : "";

  if (fullAddress || pinCode) {
    const wrapped = doc.splitTextToSize(
      fullAddress + pinCode,
      pageW / 2 + 10
    );

    doc.text(wrapped, MARGIN_LEFT + 2, y + 12);

    rowHeight += wrapped.length * 3.5;
  }

  // ---------------- RIGHT ----------------

  const mrpStartX = MARGIN_LEFT + 143;
  const rightSectionStart = mrpStartX + 2;

  const customerInfo = [
    { label: "Ph:", value: data.saleto?.phone },
    { label: "GSTIN:", value: data.saleto?.gstnumber },
    { label: "Reference No:", value: data.saleto?.referenceid },
    { label: "FSSAI:", value: data.saleto?.fssai },
    { label: "CIN:", value: data.saleto?.cinnumber },
    { label: "PAN:", value: data.saleto?.pannumber },
  ].filter(item => item.value);

  let infoY = y + 4;

  customerInfo.forEach(({ label, value }) => {

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7.5);
    doc.text(label, rightSectionStart, infoY);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      String(value),
      rightSectionStart + doc.getTextWidth(label) + 1,
      infoY
    );

    infoY += 4.5;
  });

  // Increase height if right side is taller
  rowHeight = Math.max(rowHeight, infoY - y + 1);

  // Draw divider AFTER calculating final height
  doc.line(mrpStartX, y, mrpStartX, y + rowHeight);

  // Draw outer rectangle
  doc.rect(
    MARGIN_LEFT,
    y,
    MARGIN_RIGHT - MARGIN_LEFT,
    rowHeight
  );

  y += rowHeight;
}
  /* -------------------------------------------------
     PRODUCT TABLE1
  ------------------------------------------------- */

  const rowCount = data.saleitems.length;
  const standardHeight = 4;
  const targetTotalHeight = 40;
  const extraSpace =
    rowCount > 0 && rowCount <= 5
      ? targetTotalHeight - rowCount * standardHeight
      : 0;
  autoTable(doc, {
    startY: y,
    theme: "plain",
    rowPageBreak: "avoid",
    margin: {
      top: PADDING,
      left: PADDING,
      right: PADDING,
      bottom: pageH - HALF_PAGE_HEIGHT + PADDING,
    },
    styles: {
      font: "Inter-Regular",
      fontSize: 6.5,
      cellPadding: 1.5,
      valign: "top",
      lineWidth: 0, // No default borders
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      fillColor: selectedTheme?.templateTheme?.primaryColor || [216, 27, 96], // Pink like image
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      lineWidth: 0.2,
      lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    },
    bodyStyles: {
      minCellHeight: Math.max(2, standardHeight),
      valign: "top",
    },
    head: [["#", "ITEM", "RATE", "QTY", "AMOUNT"]],
    body: data.saleitems.map((it, i) => [
      i + 1,
      it.hsnsac ? `${it.title}\nHSN: ${it.hsnsac}` : it.title,

      `₹${it.baseprice}`,
      `${it.quantity} ${(() => {
        const unitCode = it?.isSecondaryUnit
          ? it?.unitConfig?.secondaryUnitCode
          : it?.unitConfig?.primaryUnitCode;
        return unitCode || "";
      })()}`,
      `₹${it.lineTotalBasePrice}`,
      `₹${it.lineGrandTotal}`,
    ]),
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 15, halign: "right" },
      3: { cellWidth: 16, halign: "right" },
      4: { cellWidth: 16, halign: "right" },
    },
    didParseCell: function (data) {
      if (data.section === "head") {
        const colId = data.column.index;
        if (colId === 0 || colId === 3) data.cell.styles.halign = "center";
        if (colId === 2 || colId === 4 || colId === 5)
          data.cell.styles.halign = "right";
      }
      if (data.section === "body") {
        data.cell.styles.lineWidth = {
          left: 0.1,
          right: 0.1,
          top: 0,
          bottom: 0,
        };
        data.cell.styles.lineColor = selectedTheme?.templateTheme
          ?.borderColor || [200, 200, 200]; // Black vertical lines
        if (data.row.index === rowCount - 1) {
          data.cell.styles.minCellHeight = standardHeight + extraSpace;
        }
        if (data.row.index === rowCount - 1) {
          data.cell.styles.lineWidth.bottom = 0.1;
        }
      }
    },
    didDrawPage: function (data) {
      const finalY = data.cursor.y;
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
      doc.line(PADDING, finalY, pageW - PADDING, finalY);
    },
  });
  let finalY = doc.lastAutoTable.finalY;

  const footerRowHeight = 4; // Height of the "Total" bar
  const bottomSectionHeight = 20;
  const requiredHeight = footerRowHeight + bottomSectionHeight;
  const maxY = HALF_PAGE_HEIGHT - PADDING;
  if (finalY + requiredHeight > maxY) {
    doc.addPage();
    finalY = PADDING;
  }
  const borderColor = selectedTheme?.templateTheme?.borderColor || [
    200, 200, 200,
  ];
  const textY = finalY + footerRowHeight / 2 + 1;
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.1);

  doc.rect(PADDING, finalY, pageW - PADDING * 2, footerRowHeight);
  const totalLabelX = MARGIN_RIGHT - 42 - 5;
  const totalValueX = MARGIN_RIGHT - 16;
  // Vertical line to separate "In Words" from "Total"
  doc.line(totalLabelX, finalY, totalLabelX, finalY + footerRowHeight);
  // Vertical line to separate "Total" label from "Value"
  doc.line(totalValueX, finalY, totalValueX, finalY + footerRowHeight);

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(7);
  doc.text("In Words:", PADDING + 2, textY);
  doc.setFont("Inter-Regular", "normal");
  const words = data?.amountinwords || "";
  doc.text(words, PADDING + 15, textY, {
    maxWidth: totalLabelX - PADDING - 17,
  });
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(6.5);
  doc.text("TOTAL", totalLabelX + 25, textY, { align: "right" });
  doc.text(`₹${data?.grandtotal}`, MARGIN_RIGHT - 1, textY, { align: "right" });

  // --- 2. PAYMENT & SIGNATURE SECTION (Below the total bar) ---
  let bottomSectionY = finalY + footerRowHeight;
  let sigTextY;
  if (bottomSectionY + bottomSectionHeight > HALF_PAGE_HEIGHT - PADDING) {
    doc.addPage();
   
    bottomSectionY = PADDING;
    sigTextY = bottomSectionHeight - 2;
  } else {
    sigTextY = finalY + bottomSectionHeight - 2;
  } // const bottomSectionHeight = 20;
const sectionY = bottomSectionY + 5;
//leftside 
const qrSize = 18;
 let currentBankY = sectionY ;
if (storeSettings?.paymentSettings?.bankDetails && optData?.isBankDetails) {
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text("Bank Details:", PADDING , sectionY);

  const bankInfo = [
    ["Name:", storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || ""],
    ["Account #:", storeSettings?.paymentSettings?.bankDetails?.accountNo || ""],
    ["IFSC:", storeSettings?.paymentSettings?.bankDetails?.ifsc || ""],
    ["UPI ID:", storeSettings?.paymentSettings?.upiId || ""],
  ];

 currentBankY+=4

  bankInfo.forEach(([label, value]) => {
    doc.setFont("Inter-Regular", "normal");
    doc.text(label, PADDING , currentBankY);
    doc.text(String(value), PADDING  + 18, currentBankY);
    currentBankY += 3.8;
  });


  // QR Code
  const upiId = storeSettings?.paymentSettings?.upiId;
  const beneficiaryName =
    storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";

  if (upiId) {
    try {
      const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
        beneficiaryName
      )}&cu=INR`;

      const qrCodeDataUrl = await QRCode.toDataURL(upiUrl);

const qrX = PADDING + 55;

      doc.addImage(
        qrCodeDataUrl,
        "PNG",
        qrX,
        sectionY,
        qrSize,
        qrSize
      );
    } catch (err) {
      console.warn(err);
    }
  }
}
  // Right SIDE: Payment Details
  let currentPayY = sectionY
  const paymentData = [
    { amount: data?.cashPaid || 0, label: "Cash", val: data?.cashPaid },
    { amount: data?.cardPaid || 0, label: "Card", val: data?.cardPaid },
    { amount: data?.upiPaid || 0, label: "UPI", val: data?.upiPaid },
    { amount: data?.onlinePaid || 0, label: "Online", val: data?.onlinePaid },
  ].filter((p) => p.amount > 0);

 if (optData?.isPayment) {
 const paymentStartX = pageW - 80; // Adjust this value if needed

paymentData.forEach((pay, index) => {
  // Keep the same 2-column layout
  const xPos = index % 2 === 0 ? paymentStartX : paymentStartX + 45;
  const yPos = currentPayY + Math.floor(index / 2) * 5;

  doc.setFont("Inter-Bold", "bold");
  doc.text(`₹${pay.val}`, xPos, yPos);

  doc.setFont("Inter-Regular", "normal");
  doc.text(
    ` Paid via ${pay.label}`,
    xPos + doc.getTextWidth(`₹${pay.val}`) + 1,
    yPos
  );

  });
 }


 // =====================================
// REMARK SECTION
// =====================================

const bankEndY = currentBankY;
const paymentEndY = sectionY + Math.ceil(paymentData.length / 2) * 5;

let remarkStartY = sectionY;
let qrEndY = sectionY;
if (optData?.isBankDetails && optData?.isPayment) {
  remarkStartY = Math.max(bankEndY, paymentEndY, qrEndY) + 4;
} else if (optData?.isBankDetails) {
  remarkStartY = Math.max(bankEndY, qrEndY) + 4;
} else if (optData?.isPayment) {
  remarkStartY = paymentEndY + 4;
} else {
  remarkStartY = sectionY;
}
if (optData?.isRemark && data?.remark?.trim()) {

  const remarkWidth = pageW - (PADDING * 2);

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);

  // Check space
  const remarkLines = doc.splitTextToSize(
    capitalizeFirstLetter(data.remark),
    remarkWidth - 6
  );

  const lineHeight = 3.5;
    const paddingTop = 2;
  const paddingBottom = 2;
    const remarkHeight =
    (remarkLines.length * lineHeight) + paddingTop + paddingBottom;

  if (remarkStartY + remarkHeight + 10 > HALF_PAGE_HEIGHT) {
    doc.addPage();
    remarkStartY = PADDING+2;
  }

  // Heading
  doc.text("Remark", PADDING, remarkStartY);

  // Border
  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor || 180
  );
  doc.setLineWidth(0.2);

  // Text
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);

  let txtY = remarkStartY + 3;

  remarkLines.forEach(line => {
    doc.text(line, PADDING , txtY);
    txtY += lineHeight;
  });

  remarkStartY += remarkHeight + 4;
} 
let signatureY = remarkStartY ;
if (signatureY + 20 > HALF_PAGE_HEIGHT) {
  doc.addPage();
  signatureY = PADDING + 2;
}

const signMaxWidth = 25;
const signX = pageW - PADDING - signMaxWidth;

if (data?.signature) {
  try {
    const signimg = uRLs.IMAGE_URL + data.signature;
    const base64 = await urlToBase64(signimg);
    const { width, height } = await loadImageDimensions(base64);

    const maxWidth = 25;
    const maxHeight = 10;
    const ratio = Math.min(maxWidth / width, maxHeight / height);

    const imgW = width * ratio;
    const imgH = height * ratio;

    doc.addImage(
      base64,
      signimg.endsWith(".png") ? "PNG" : "JPEG",
      signX + (maxWidth - imgW) / 2,
      signatureY,
      imgW,
      imgH
    );

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(6);
    doc.text(
      "Authorised Signature",
      signX + maxWidth / 2,
      signatureY + imgH + 4,
      { align: "center" }
    );
  } catch (e) {
    console.warn("Signature failed", e);
  }
}
  if (data?.negobillBranding) {
    // const finalBrandingY = remarkStartY + 3;
    const minGapY = y + 3;
    const bottomLimitY = HALF_PAGE_HEIGHT - 5;
    let finalBrandingY = Math.max(minGapY, bottomLimitY);
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);
    doc.text(normalText, MARGIN_LEFT, finalBrandingY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, MARGIN_LEFT + normalW, finalBrandingY);
  }

  return doc;
};

const CompactInvoice = ({ data, selectedTheme, selectedSize, optData }) => {
  const { storeSettings } = usePermission();
  const { settings } = useAuth();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const globalData = FormatedSaleData(data);
  const saleDate = globalData?.saledate;
  const timezone = globalData?.saleby?.timezone;
  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }
  const isInterState =
    globalData?.salefrom?.statecode &&
    globalData?.saleto?.statecode &&
    globalData?.salefrom?.statecode !== globalData?.saleto?.statecode;
  const isBillReceipt =
    data?.salemode?.toUpperCase() === "BILL" &&
    storeSettings?.saleSettings?.saleModeSettings?.isBill_isTax;
  const documentTitle = isBillReceipt ? "BILL RECEIPT" : "TAX INVOICE";
  const invLabels = isBillReceipt ? "Bill No:" : "Invoice No:";
  useEffect(() => {
          const upi = storeSettings?.paymentSettings?.upiId;
      
          const name = storeSettings?.paymentSettings?.bankDetails?.beneficiaryName;
          if (!upi) return; // skip until upi is available
      
          const upiUrl = `upi://pay?pa=${upi}&pn=${encodeURIComponent(name)}&cu=INR`;
          console.log("ip", upiUrl);
          QRCode.toDataURL(upiUrl)
            .then((url) => {
              setQrCodeDataUrl(url);
            })
            .catch((err) => {
              console.error("QR Code generation failed: ", err);
            });
        }, [
          storeSettings?.paymentSettings?.upiId,
          storeSettings?.paymentSettings?.bankDetails?.beneficiaryName,
        ]);     
  return (
    <>
      {globalData?.salemode === "INVOICE" || isBillReceipt ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in p-2">
                <div style={{ display: "flex", paddingBottom: "10px" }}>
                  <div style={{ flex: "1" }}>
                    <div>
                      {("/assets/images/logo-light.png" ||
                        settings?.saleProfileData?.image) && (
                        <img
                          src={
                            settings?.saleProfileData?.image
                              ? uRLs.IMAGE_URL +
                                "profile/" +
                                settings?.saleProfileData?.image
                              : "/assets/images/logo-light.png"
                          }
                          className="img-rounded logo"
                          height="40"
                          alt="Logo"
                        />
                      )}
                    </div>

                    <div className="font-12">
                      {settings?.saleProfileData?.addressline1}
                    </div>
                    <div className="font-12">
                      {settings?.saleProfileData?.addressline2}{" "}
                      {settings?.saleProfileData?.pincode || ""}
                    </div>
                    <div className="font-12">
                      <span className="fw-bold">Ph:</span>{" "}
                      {settings?.saleProfileData?.phone}{" "}
                      <span className="fw-bold">GSTIN:</span>{" "}
                      {settings?.saleProfileData?.gstnumber}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "center" }}>
                    <div className="font-14" style={{ color: "#555" }}>
                      {documentTitle}
                    </div>
                    <div
                      className="font-15"
                      style={{
                        marginTop: "5px",
                        fontWeight: "800",
                        textTransform: "uppercase",
                        lineHeight: "1",
                      }}
                    >
                      {settings?.saleProfileData?.title || "COMPANY NAME"}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "right" }}>
                    <div
                      style={{ display: "inline-block", textAlign: "right" }}
                    >
                      <div
                        className=" font-10"
                        style={{
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          marginBottom: "4px",
                        }}
                      >
                        {invLabels}
                        <span className="fw-bold"> {globalData?.saleno}</span>
                      </div>
                      <div className="d-flex justify-content-end font-10">
                        {/* <span>DATE: {saleTime?.date}</span> */}
                        <span> DATE: {saleTime?.date}</span>
                        <span className="ms-3">TIME:{saleTime?.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {optData?.isCustomerDisplay && (
                  <div
                    className="d-flex "
                    style={{
                      border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                    }}
                  >
                    <div
                      style={{
                        flex: "1",
                        padding: "5px",
                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                      }}
                    >
                      <span className="fw-bold font-12">
                        Customer Details :
                      </span>
                      <div className="font-12 ">{globalData?.saleto?.name}</div>
                      <div className="font-12">
                        {" "}
                        {globalData?.saleto?.addressline1 &&
                          `${globalData?.saleto?.addressline1}, `}
                        {globalData?.saleto?.addressline2 &&
                          `${globalData?.saleto?.addressline2}, `}
                        {globalData?.saleto?.district &&
                          `${globalData?.saleto?.district}, `}
                        {globalData?.saleto?.pincode &&
                          ` - ${globalData?.saleto?.pincode}`}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "280px",
                        padding: "5px",
                        WebkitFontSmoothing: "antialiased",
                        textRendering: "optimizeLegibility",
                      }}
                    >
                      <div className="font-12">
                        <span className="fw-bold">Ph:</span>{" "}
                        {globalData?.saleto?.phone || ""}
                      </div>
                     <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                GSTIN:
                                              </span>{" "}
                                              {globalData?.saleto?.gstnumber ||
                                                ""}
                                            </div> 
                                             {globalData?.saleto?.pannumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                PAN:
                                              </span>{" "}
                                              {globalData?.saleto?.pannumber ||
                                                ""}
                                            </div>
                                            )}
                                            {globalData?.saleto?.fssai &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                FASSI:
                                              </span>{" "}
                                              {globalData?.saleto?.fssai ||
                                                ""}
                                            </div>
                                            )}
                                              {globalData?.saleto?.cinnumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                CIN:
                                              </span>{" "}
                                              {globalData?.saleto?.cinnumber ||
                                                ""}
                                            </div>
                                              )}
                                            {globalData?.saleto?.referenceid &&(
                                              <div className=" font-12">
                                              {" "}
                                              <span className="">
                                                Reference No:
                                              </span>{" "}
                                              {globalData?.saleto?.referenceid ||
                                                ""}
                                            </div>
                                              )}
                    </div>
                  </div>
                )}
                <table
                  width="100%"
                  className="mt-0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr className=" fw-bold">
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "40px",

                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "40px" }}
                      >
                        {" "}
                        #
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          textAlign: "left",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        // style={{ border: "1px solid ", padding: "5px", textAlign: "left" }}
                      >
                        ITEM
                      </th>

                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "80px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ", padding: "5px", width: "80px" }}
                      >
                        RATE
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "60px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "60px" }}
                      >
                        QTY
                      </th>
                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "60px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "100px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        LANDING COST
                      </th>
                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "60px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "80px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        TAX VAL
                      </th>
                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "100px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "100px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalData?.saleitems?.map((item, index) => (
                      <tr
                        key={index}
                        className="font-13"
                        style={{ height: "30px" }}
                      >
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,

                            textAlign: "center",
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            padding: "0 5px",
                          }}
                        >
                          {item?.title}
                        </td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          ₹{item?.baseprice}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "center",
                          }}
                        >
                          {item?.quantity}
                          {(() => {
                            const unitCode = item?.isSecondaryUnit
                              ? item?.unitConfig?.secondaryUnitCode
                              : item?.unitConfig?.primaryUnitCode;
                            return unitCode || "";
                          })()}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "center",
                          }}
                        >
                          ₹{item?.lineTotalLandingCost}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "center",
                          }}
                        >
                          ₹{item?.lineTotalBasePrice}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          ₹{item?.lineGrandTotal}
                        </td>
                      </tr>
                    ))}

                    {[
                      ...Array(
                        Math.max(0, 5 - (globalData?.saleitems?.length || 0)),
                      ),
                    ].map((_, i) => (
                      <tr key={`empty-${i}`} style={{ height: "50px" }}>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Row 1: In Words & Total */}
                    <tr className="font-10">
                      <td
                        colSpan="4"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "left",
                          padding: "5px",
                        }}
                      >
                        <span className="fw-bold">In Words:</span>{" "}
                        {globalData?.amountinwords}
                      </td>
                      <td
                        colSpan="2"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        TOTAL
                      </td>
                      <td
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        ₹{globalData?.grandtotal}
                      </td>
                    </tr>
                    <tr className="font-10">
                      <td
                        colSpan="4"
                        rowSpan={isInterState ? 1 : 2}
                        style={{
                          textAlign: "left",
                          
                          verticalAlign: "top",
                        }}
                      >
                        <div className="font-12 mt-2">
                          {optData?.isPayment && (
                        <div className="row g-2 m-0">
                          {globalData?.cashPaid > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>₹{globalData.cashPaid}</b> Paid via Cash
                              </span>
                            </div>
                          )}
                          {globalData?.upiPaid > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>₹{globalData.upiPaid}</b> Paid via UPI
                              </span>
                            </div>
                          )}
                          {globalData?.cardPaid > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>₹{globalData.cardPaid}</b> Paid via Card
                              </span>
                            </div>
                          )}
                          {globalData?.onlinePaid > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>₹{globalData.onlinePaid}</b> Paid via Online
                              </span>
                            </div>
                          )}
                        </div>
                         )}
                        </div>
                      </td>

                      {isInterState ? (
                        <>
                          <td
                            colSpan="2"
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "left",
                              padding: "5px",
                              verticalAlign: "top",
                            }}
                          >
                            IGST
                          </td>
                          <td
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                            }}
                          >
                            ₹{globalData?.igstamount || 0}
                          </td>
                        </>
                      ) : (
                        <>
                          <td
                            colSpan="2"
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                            }}
                          >
                            CGST
                          </td>
                          <td
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                            }}
                          >
                            ₹{globalData?.cgstamount || 0}
                          </td>
                        </>
                      )}
                    </tr>

                    {!isInterState && (
                      <tr className="font-10">
                        <td
                          colSpan="2"
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            padding: "5px",
                          }}
                        >
                          SGST
                        </td>
                        <td
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            padding: "5px",
                          }}
                        >
                          ₹{globalData?.sgstamount || 0}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>                
                <div className="row mt-2">
                  <div className="col-7 d-flex ">
                    <div className="">
            {storeSettings?.paymentSettings?.bankDetails && optData?.isBankDetails && (
                         <>
                          <strong>Bank Details:</strong>
                           <div className="d-flex font-11">
                               <span style={{ width: "80px" }} >
                                Name:
                                </span>
                                 { storeSettings?.paymentSettings?.bankDetails?.beneficiaryName}
                          </div>
                           <div className="d-flex font-11">
                             <span style={{ width: "80px" }}>
                              Account #:
                             </span>
                             {storeSettings?.paymentSettings?.bankDetails?.accountNo}
                            </div>
                           <div className="d-flex font-11">
                           <span  style={{ width: "80px" }} >
                             IFSC:
                           </span>
                           {storeSettings?.paymentSettings?.bankDetails?.ifsc}
                            </div>
                          <div className="d-flex font-11">
                          <span style={{ width: "80px" }} >
                           UPI ID:
                           </span>
                           {storeSettings?.paymentSettings?.upiId}
                           </div>{" "}
                        </>
                       )}
                       </div>
                       {qrCodeDataUrl &&  optData?.isBankDetails && (
                       <div className="col-3 ms-3 d-flex align-items-center justify-content-end">
                         <img
                           src={qrCodeDataUrl}
                          alt="QR"
                         style={{width: "100px",height: "100px",}}
                         />
                      </div>
                    )}   
                  </div>
                  <div className="col-5 ">
                    <div className="font-12 mt-2  text-end">
                          {storeSettings?.invoiceSettings?.signature ? (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              alt="Signature"
                              width="120"
                              style={{ marginBottom: "-5px" }}
                            />
                          ) : (
                            <div className="pt-4"></div>
                          )}
                          <div style={{ paddingTop: "5px" }}>
                            Authorised Signature
                          </div>
                        </div>
                  </div>
                </div>
 {optData?.isRemark && globalData?.remark ? (
                      <>
                      <div className="font-12 mt-2 fw-600 text-black mb-1">
                       Remark
                      </div>
                    
                      <div
                      className="font-12 mb-2 p-0"
                  style={{                
                whiteSpace: "pre-wrap",
               wordBreak: "break-word",
              }}
               >
              {capitalizeFirstLetter(globalData?.remark)}
               </div>
              </>
                ) : null} 
                {storeSettings?.negobillBranding && (
                  <>
                    <div className="mt-5 font-12 d-flex justify-content-left ">
                      <span>
                        Powered By <span className="fw-bold">NegoBill.com</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : globalData?.salemode === "BILL" ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in p-2">
                <div style={{ display: "flex", paddingBottom: "10px" }}>
                  <div style={{ flex: "1" }}>
                    <div>
                      {("/assets/images/logo-light.png" ||
                        settings?.saleProfileData?.image) && (
                        <img
                          src={
                            settings?.saleProfileData?.image
                              ? uRLs.IMAGE_URL +
                                "profile/" +
                                settings?.saleProfileData?.image
                              : "/assets/images/logo-light.png"
                          }
                          className="img-rounded logo"
                          height="40"
                          alt="Logo"
                        />
                      )}
                    </div>

                    <div className="font-12">
                      {settings?.saleProfileData?.addressline1}
                    </div>
                    <div className="font-12">
                      {settings?.saleProfileData?.addressline2}{" "}
                      {settings?.saleProfileData?.pincode || ""}
                    </div>
                    <div className="font-12">
                      <span className="fw-bold">Ph:</span>{" "}
                      {settings?.saleProfileData?.phone}{" "}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "center" }}>
                    <div className="font-14" style={{ color: "#555" }}>
                      BILL RECEIPT
                    </div>
                    <div
                      className="font-15"
                      style={{
                        marginTop: "5px",
                        fontWeight: "800",
                        textTransform: "uppercase",
                        lineHeight: "1",
                      }}
                    >
                      {settings?.saleProfileData?.title || "COMPANY NAME"}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "right" }}>
                    <div
                      style={{ display: "inline-block", textAlign: "right" }}
                    >
                      <div
                        className=" font-10"
                        style={{
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          marginBottom: "4px",
                        }}
                      >
                        Invoice No.
                        <span className="fw-bold"> {globalData?.saleno}</span>
                      </div>
                      <div className="d-flex justify-content-end font-10">
                        {/* <span>DATE: {saleTime?.date}</span> */}
                        <span> DATE: {saleTime?.date}</span>
                        <span className="ms-3">TIME:{saleTime?.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {optData?.isCustomerDisplay && (
                  <div
                    className="d-flex "
                    style={{
                      border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                    }}
                  >
                    <div
                      style={{
                        flex: "1",
                        padding: "5px",
                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                      }}
                    >
                      <span className="fw-bold font-12">
                        Customer Details :
                      </span>
                      <div className="font-12 ">{globalData?.saleto?.name}</div>
                      <div className="font-12">
                        {" "}
                        {globalData?.saleto?.addressline1 &&
                          `${globalData?.saleto?.addressline1}, `}
                        {globalData?.saleto?.addressline2 &&
                          `${globalData?.saleto?.addressline2}, `}
                        {globalData?.saleto?.district &&
                          `${globalData?.saleto?.district}, `}
                        {globalData?.saleto?.pincode &&
                          ` - ${globalData?.saleto?.pincode}`}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "270px",
                        padding: "5px",
                        WebkitFontSmoothing: "antialiased",
                        textRendering: "optimizeLegibility",
                      }}
                    >
                      <div className="font-12">
                        <span className="fw-bold">Ph:</span>{" "}
                        {globalData?.saleto?.phone || ""}
                      </div>
                       <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                GSTIN:
                                              </span>{" "}
                                              {globalData?.saleto?.gstnumber ||
                                                ""}
                                            </div> 
                                             {globalData?.saleto?.pannumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                PAN:
                                              </span>{" "}
                                              {globalData?.saleto?.pannumber ||
                                                ""}
                                            </div>
                                            )}
                                            {globalData?.saleto?.fssai &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                FASSI:
                                              </span>{" "}
                                              {globalData?.saleto?.fssai ||
                                                ""}
                                            </div>
                                            )}
                                              {globalData?.saleto?.cinnumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                CIN:
                                              </span>{" "}
                                              {globalData?.saleto?.cinnumber ||
                                                ""}
                                            </div>
                                              )}
                                            {globalData?.saleto?.referenceid &&(
                                              <div className=" font-12">
                                              {" "}
                                              <span className="">
                                                Reference No:
                                              </span>{" "}
                                              {globalData?.saleto?.referenceid ||
                                                ""}
                                            </div>
                                              )}
                    </div>
                  </div>
                )}
                <table
                  width="100%"
                  className="mt-0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr className=" fw-bold">
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "40px",

                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "40px" }}
                      >
                        {" "}
                        #
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          textAlign: "left",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        // style={{ border: "1px solid ", padding: "5px", textAlign: "left" }}
                      >
                        ITEM
                      </th>

                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "80px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ", padding: "5px", width: "80px" }}
                      >
                        RATE
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "60px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "60px" }}
                      >
                        QTY
                      </th>

                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "100px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "100px",
                          textAlign: "right",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalData?.saleitems?.map((item, index) => (
                      <tr
                        key={index}
                        className="font-13"
                        style={{ height: "30px" }}
                      >
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,

                            textAlign: "center",
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            padding: "0 5px",
                          }}
                        >
                          {item?.title}
                        </td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          ₹{item?.baseprice}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "center",
                          }}
                        >
                          {item?.quantity}
                          {(() => {
                            const unitCode = item?.isSecondaryUnit
                              ? item?.unitConfig?.secondaryUnitCode
                              : item?.unitConfig?.primaryUnitCode;
                            return unitCode || "";
                          })()}
                        </td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          ₹{item?.lineGrandTotal}
                        </td>
                      </tr>
                    ))}

                    {[
                      ...Array(
                        Math.max(0, 5 - (globalData?.saleitems?.length || 0)),
                      ),
                    ].map((_, i) => (
                      <tr key={`empty-${i}`} style={{ height: "50px" }}>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Row 1: In Words & Total */}
                    <tr className="font-10">
                      <td
                        colSpan="2"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "left",
                          padding: "5px",
                        }}
                      >
                        <span className="fw-bold">In Words:</span>{" "}
                        {globalData?.amountinwords}
                      </td>
                      <td
                        colSpan="2"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        TOTAL
                      </td>
                      <td
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        ₹{globalData?.grandtotal}
                      </td>
                    </tr>
                    {/* <tr className="font-10">
                      <td
                        colSpan="4"
                        style={{
                          textAlign: "left",
                          padding: "5px",
                          verticalAlign: "top",
                        }}
                      >
                         {optData?.isPayment && (
                        <div className="row g-2 m-0">
                          {globalData?.cashPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.cashPaid}</b> Paid via Cash
                              </span>
                            </div>
                          )}
                          {globalData?.upiPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.upiPaid}</b> Paid via UPI
                              </span>
                            </div>
                          )}
                          {globalData?.cardPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.cardPaid}</b> Paid via Card
                              </span>
                            </div>
                          )}
                          {globalData?.onlinePaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.onlinePaid}</b> Paid via Online
                              </span>
                            </div>
                          )}
                        </div>
                         )}
                      </td>
                      <td
                        colSpan="2"
                        className="p-0"
                        style={{
                          textAlign: "left",
                          verticalAlign: "bottom",
                        }}
                      >
                        <div className="font-12 mt-0 border p-2  ">
                          {storeSettings?.invoiceSettings?.signature ? (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              alt="Signature"
                              width="120"
                              style={{ marginBottom: "-5px" }}
                            />
                          ) : (
                            <div className="pt-4"></div>
                          )}
                          <div style={{ paddingTop: "5px" }}>
                            Authorised Signature
                          </div>
                        </div>
                      </td>
                    </tr> */}
                  </tfoot>
                </table>
                 <div className="row mt-2">
                  <div className="col-7 d-flex ">
                    <div className="">
            {storeSettings?.paymentSettings?.bankDetails && optData?.isBankDetails && (
                         <>
                          <strong>Bank Details:</strong>
                           <div className="d-flex font-11">
                               <span style={{ width: "80px" }} >
                                Name:
                                </span>
                                 { storeSettings?.paymentSettings?.bankDetails?.beneficiaryName}
                          </div>
                           <div className="d-flex font-11">
                             <span style={{ width: "80px" }}>
                              Account #:
                             </span>
                             {storeSettings?.paymentSettings?.bankDetails?.accountNo}
                            </div>
                           <div className="d-flex font-11">
                           <span  style={{ width: "80px" }} >
                             IFSC:
                           </span>
                           {storeSettings?.paymentSettings?.bankDetails?.ifsc}
                            </div>
                          <div className="d-flex font-11">
                          <span style={{ width: "80px" }} >
                           UPI ID:
                           </span>
                           {storeSettings?.paymentSettings?.upiId}
                           </div>{" "}
                        </>
                       )}
                       </div>
                       {qrCodeDataUrl &&  optData?.isBankDetails && (
                       <div className="col-3 ms-3 d-flex align-items-center justify-content-end">
                         <img
                           src={qrCodeDataUrl}
                          alt="QR"
                         style={{width: "100px",height: "100px",}}
                         />
                      </div>
                    )}   
                  </div>
                  <div className="col-5">
                     {optData?.isPayment && (
                        <div className="row g-2 m-0">
                          {globalData?.cashPaid > 0 && (
                            <div className="col-6 font-12 ps-1 mb-1">
                              <span>
                                <b>₹{globalData.cashPaid}</b> Paid via Cash
                              </span>
                            </div>
                          )}
                          {globalData?.upiPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.upiPaid}</b> Paid via UPI
                              </span>
                            </div>
                          )}
                          {globalData?.cardPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.cardPaid}</b> Paid via Card
                              </span>
                            </div>
                          )}
                          {globalData?.onlinePaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>₹{globalData.onlinePaid}</b> Paid via Online
                              </span>
                            </div>
                          )}
                        </div>
                         )}
                  </div>
                  </div>
                   {optData?.isRemark && globalData?.remark ? (
                      <>
                      <div className="font-12 mt-2 fw-600 text-black mb-1">
                       Remark
                      </div>
                    
                      <div
                      className="font-12 mb-2 p-0"
                  style={{               
                whiteSpace: "pre-wrap",
               wordBreak: "break-word",
              }}
               >
              {capitalizeFirstLetter(globalData?.remark)}
               </div>
              </>
                ) : null} 
                  <div className="row mt-0">
                     <div className="col-12">
                    <div className="font-12 mt-2  text-end">
                          {storeSettings?.invoiceSettings?.signature ? (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              alt="Signature"
                              width="120"
                              style={{ marginBottom: "-5px" }}
                            />
                          ) : (
                            <div className="pt-4"></div>
                          )}
                          <div style={{ paddingTop: "5px" }}>
                            Authorised Signature
                          </div>
                        </div>
                  </div>
                  </div>

                {storeSettings?.negobillBranding && (
                  <>
                    <div className="mt-5 font-12 d-flex justify-content-left ">
                      <span>
                        Powered By <span className="fw-bold">NegoBill.com</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
    </>
  );
};

export default CompactInvoice;
